import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Checkbox,
    FormControlLabel,
    Alert,
    Chip,
    CircularProgress,
    IconButton
} from '@mui/material';
import { styled } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import {
    ExpandMore as ExpandMoreIcon,
    Description as DescriptionIcon,
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { legalComplianceApi } from '../../services/api';
import { showSnackbar } from '../SnackBar/SnackBar';

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: '12px',
        maxWidth: '700px',
        width: '100%',
        margin: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            margin: theme.spacing(1),
            maxHeight: '95vh',
        }
    },
}));

const DialogTitleStyled = styled(DialogTitle)(({ theme }) => ({
    color: '#2C5283',
    fontWeight: 600,
    fontSize: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #e0e0e0',
    backgroundColor: '#f8f9fa',
    padding: theme.spacing(2, 3),
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    border: '1px solid #e0e0e0',
    borderRadius: '8px !important',
    '&:before': {
        display: 'none',
    },
    '&.Mui-expanded': {
        margin: `0 0 ${theme.spacing(2)}px 0`,
    }
}));

const DocumentContent = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: '#f9f9f9',
    borderRadius: '4px',
    maxHeight: '300px',
    overflow: 'auto',
    '& h1, & h2, & h3': {
        color: '#2C5283',
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },
    '& p': {
        marginBottom: theme.spacing(1.5),
        lineHeight: 1.6,
    },
    '& ul, & ol': {
        marginLeft: theme.spacing(3),
        marginBottom: theme.spacing(1.5),
    },
    [theme.breakpoints.down('sm')]: {
        maxHeight: '200px',
    }
}));

const SummaryBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: '#e3f2fd',
    borderRadius: '8px',
    marginBottom: theme.spacing(2),
    border: '1px solid #90caf9',
}));

/**
 * LegalComplianceModal Component
 *
 * Production-ready modal for handling legal document acceptance.
 * Signature is optional - users just need to check the acceptance box.
 * Implements Kenya Data Protection Act 2019 compliance requirements.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether modal is open
 * @param {Array} props.pendingDocuments - Documents requiring acceptance
 * @param {Object} props.userData - User data (tenant_id/landlord_id, user type)
 * @param {Function} props.onAccepted - Callback when documents are accepted
 * @param {Function} props.onClose - Callback when modal should close (only allowed after acceptance)
 */
const LegalComplianceModal = ({
    open,
    pendingDocuments = [],
    userData,
    onAccepted,
    onClose
}) => {
    const [expandedDocs, setExpandedDocs] = useState([]);
    const [accepted, setAccepted] = useState(false);
    const [loading, setLoading] = useState(false);

    // Reset state when modal opens
    useEffect(() => {
        if (open) {
            setExpandedDocs([]);
            setAccepted(false);
        }
    }, [open]);

    const handleAccordionChange = (docId) => (event, isExpanded) => {
        setExpandedDocs(prev =>
            isExpanded
                ? [...prev, docId]
                : prev.filter(id => id !== docId)
        );
    };


    const validateForm = () => {
        // Check acceptance checkbox
        if (!accepted) {
            showSnackbar({
                title: "Acceptance Required!",
                message: "Please check the box to confirm you accept all documents.",
                type: "warning",
                position: { vertical: "bottom", horizontal: "right" },
            });
            return false;
        }

        return true;
    };

    const handleAccept = async () => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            // Accept documents (no signature required)
            const documentIds = pendingDocuments.map(doc => ({
                document_id: doc.document_id
            }));

            const acceptResponse = await legalComplianceApi.acceptDocuments({
                user_id: userData.user_id,
                user_type: userData.user_type,
                document_ids: documentIds,
                signature_id: null, // No signature required
                acceptance_method: 'WEB'
            });

            if (!acceptResponse.ok || !acceptResponse.data?.success) {
                throw new Error(acceptResponse.data?.Error || 'Failed to accept documents');
            }

            // Success!
            showSnackbar({
                title: "Success!",
                message: "Legal documents accepted successfully. Welcome to IT-Orama!",
                type: "success",
                position: { vertical: "bottom", horizontal: "right" },
            });

            // Call success callback
            if (onAccepted) {
                onAccepted();
            }

        } catch (error) {
            console.error('Acceptance failed:', error);
            showSnackbar({
                title: "Error!",
                message: error.message || "Failed to process your acceptance. Please try again.",
                type: "error",
                position: { vertical: "bottom", horizontal: "right" },
            });
        } finally {
            setLoading(false);
        }
    };

    const canSubmit = accepted && !loading;

    return (
        <StyledDialog
            open={open}
            onClose={null} // Non-dismissible - user must accept
            disableEscapeKeyDown
            maxWidth="md"
            fullWidth
        >
            <DialogTitleStyled>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ color: '#2C5283' }} />
                    Legal Compliance Required
                </Box>
                {/* Close button disabled - non-dismissible */}
                <IconButton size="small" disabled>
                    <CloseIcon />
                </IconButton>
            </DialogTitleStyled>

            <DialogContent sx={{ pt: 3, pb: 2 }}>
                <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                        <strong>Important:</strong> Before you can use IT-Orama, you must review and accept
                        the following legal documents in compliance with the Kenya Data Protection Act 2019.
                    </Typography>
                </Alert>

                <SummaryBox>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Documents to Review ({pendingDocuments.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {pendingDocuments.map(doc => (
                            <Chip
                                key={doc.document_id}
                                label={doc.title}
                                icon={<DescriptionIcon />}
                                size="small"
                                variant="outlined"
                                color="primary"
                            />
                        ))}
                    </Box>
                </SummaryBox>

                {/* Documents List */}
                <Box sx={{ mb: 3 }}>
                    {pendingDocuments.map((doc, index) => (
                        <StyledAccordion
                            key={doc.document_id}
                            expanded={expandedDocs.includes(doc.document_id)}
                            onChange={handleAccordionChange(doc.document_id)}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`panel-${doc.document_id}-content`}
                                id={`panel-${doc.document_id}-header`}
                            >
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        {index + 1}. {doc.title}
                                        <Chip
                                            label={`v${doc.version}`}
                                            size="small"
                                            sx={{ ml: 1 }}
                                        />
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {doc.summary}
                                    </Typography>
                                    {doc.effective_date && (
                                        <Typography variant="caption" color="textSecondary">
                                            Effective: {new Date(doc.effective_date).toLocaleDateString()}
                                        </Typography>
                                    )}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <DocumentContent
                                    dangerouslySetInnerHTML={{ __html: doc.content }}
                                />
                            </AccordionDetails>
                        </StyledAccordion>
                    ))}
                </Box>

                {/* Acceptance Checkbox */}
                <Box sx={{ mt: 3 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={accepted}
                                onChange={(e) => setAccepted(e.target.checked)}
                                color="primary"
                            />
                        }
                        label={
                            <Typography variant="body2">
                                I have read and understood all {pendingDocuments.length} document(s) above and accept them.
                            </Typography>
                        }
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' }}>
                <LoadingButton
                    loading={loading}
                    variant="contained"
                    size="large"
                    onClick={handleAccept}
                    disabled={!canSubmit}
                    sx={{
                        background: '#2C5283',
                        '&:hover': {
                            background: '#1a3a5a',
                        },
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 4
                    }}
                    fullWidth
                >
                    {loading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={20} color="inherit" />
                            Processing...
                        </Box>
                    ) : (
                        'Accept and Continue to IT-Orama'
                    )}
                </LoadingButton>
            </DialogActions>
        </StyledDialog>
    );
};

export default LegalComplianceModal;
